import { ArrowLeft, Flag, Trash2 } from 'lucide-react';
import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { ApiError } from '../../lib/api.ts';
import type { RoundSummary } from '../../lib/rounds.ts';
import { deleteRound, listRounds } from '../../lib/rounds.ts';

/* Les tours joués, et le moyen d'en effacer un.
 *
 * ── Pourquoi la suppression est là ─────────────────────────────────────────
 *
 * Un tour lancé par erreur, un essai, une partie qu'on n'a jamais vraiment
 * jouée : sans ce bouton ils restent dans les statistiques pour toujours et
 * faussent la moyenne du trou comme le meilleur score. Un historique qu'on ne
 * peut pas corriger cesse vite d'être consulté.
 *
 * ── Ce que « supprimer » veut dire dépend de qui demande ───────────────────
 *
 * L'hôte efface la partie entière — c'est la sienne. Un invité ne retire que
 * sa participation : effacer la carte des autres parce qu'on s'est trompé de
 * bouton serait hors de proportion. Le libellé de la confirmation le dit.
 */

const jour = (iso: string): string =>
    new Date(iso).toLocaleDateString('fr-FR', { day: 'numeric', month: 'long', year: 'numeric' });

export function RoundHistory() {
    const [rounds, setRounds] = useState<RoundSummary[]>([]);
    const [loading, setLoading] = useState(true);
    const [busy, setBusy] = useState(false);
    const [error, setError] = useState<string | null>(null);
    const [confirmer, setConfirmer] = useState<RoundSummary | null>(null);

    const charger = (): void => {
        void listRounds()
            .then(setRounds)
            .catch((e) => setError(e instanceof ApiError ? e.message : 'Chargement impossible'))
            .finally(() => setLoading(false));
    };
    useEffect(charger, []);

    const supprimer = async (r: RoundSummary): Promise<void> => {
        setBusy(true);
        setError(null);
        try {
            await deleteRound(r.id);
            setRounds((l) => l.filter((x) => x.id !== r.id));
            setConfirmer(null);
        } catch (e) {
            setError(e instanceof ApiError ? e.message : 'Suppression impossible');
        } finally { setBusy(false); }
    };

    if (loading) {
        return <div className="flex justify-center py-16"><span className="loading loading-ring loading-lg text-primary" /></div>;
    }

    return (
        <div className="space-y-4 py-2">
            <header>
                <Link to="/parcours" className="btn btn-ghost btn-sm mb-2 gap-2">
                    <ArrowLeft size={16} /> Parcours
                </Link>
                <h1 className="font-display text-2xl font-bold tracking-tight">Mes parcours</h1>
            </header>

            {error && <div role="alert" className="alert alert-error text-sm"><span>{error}</span></div>}

            {rounds.length === 0 ? (
                <p className="rounded-box bg-base-200 p-4 text-sm opacity-70">
                    Aucun parcours joué pour l'instant.
                </p>
            ) : (
                <ul className="space-y-3">
                    {rounds.map((r) => (
                        <li key={r.id} className="rounded-box bg-base-200 p-4">
                            <div className="flex items-start gap-3">
                                {/* Toute la ligne mène à la carte : c'est ce
                                    qu'on vient chercher, et un lien discret
                                    quelque part se raterait. */}
                                <Link to={`/parcours/tour/${r.id}`} className="min-w-0 flex-1">
                                    <span className="flex flex-wrap items-center gap-x-2 gap-y-1">
                                        <span className="text-lg font-bold leading-tight">{r.courseName}</span>
                                        {r.status === 'open' && (
                                            <span className="badge badge-warning badge-sm font-semibold">en cours</span>
                                        )}
                                    </span>
                                    <span className="mt-0.5 block text-sm opacity-70">
                                        {jour(r.startedAt)}
                                        {r.teeLabel && ` · ${r.teeLabel}`}
                                        {r.playerCount > 1 && ` · ${r.playerCount} joueurs`}
                                    </span>
                                </Link>
                                <button type="button"
                                    aria-label={`Supprimer le tour du ${jour(r.startedAt)}`}
                                    onClick={() => setConfirmer(r)}
                                    className="btn btn-ghost btn-sm btn-square shrink-0 text-error tap-target">
                                    <Trash2 size={16} />
                                </button>
                            </div>

                            <Link to={`/parcours/tour/${r.id}`}
                                className="mt-2 block border-t border-base-300 pt-2 text-sm">
                                {r.totalStrokes != null ? (
                                    <>
                                        <span className="font-score text-base">{r.totalStrokes} coups</span>
                                        <span className="opacity-70"> sur </span>
                                        <span className="font-score text-base">{r.holesPlayed}</span>
                                        <span className="opacity-70"> trou{r.holesPlayed > 1 ? 's' : ''}</span>
                                    </>
                                ) : (
                                    <span className="opacity-60">Aucun score enregistré.</span>
                                )}
                            </Link>

                            {r.status === 'open' && (
                                <Link to="/parcours/jouer"
                                    className="btn btn-accent btn-block mt-3 h-12 gap-2 tap-target">
                                    <Flag size={16} /> Reprendre
                                </Link>
                            )}
                        </li>
                    ))}
                </ul>
            )}

            {confirmer && (
                <div className="modal modal-open" role="dialog">
                    <div className="modal-box">
                        <h3 className="text-lg font-bold">
                            Supprimer ce tour à {confirmer.courseName} ?
                        </h3>
                        <p className="mt-2 text-sm opacity-80">
                            {confirmer.isHost
                                ? confirmer.playerCount > 1
                                    ? `La partie entière disparaît, avec la carte des ${confirmer.playerCount} joueurs.`
                                    : 'La carte et les coups enregistrés disparaissent.'
                                : 'Votre carte disparaît ; la partie continue pour les autres.'}
                            {' '}C'est définitif.
                        </p>
                        <div className="mt-4 space-y-2">
                            <button type="button" disabled={busy}
                                onClick={() => void supprimer(confirmer)}
                                className="btn btn-error btn-block h-14 gap-2 font-bold tap-target">
                                <Trash2 size={16} /> Supprimer
                            </button>
                            <button type="button" onClick={() => setConfirmer(null)}
                                className="btn btn-ghost btn-block tap-target">
                                Garder
                            </button>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
}
